import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Stack,
  Container,
  Typography,
  Snackbar,
  Alert,
  Box,
  IconButton,
  CircularProgress
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

//components
import Page from '../../components/Page';
import Iconify from 'src/components/Iconify';

//icons
import Scrollbar from 'src/components/Scrollbar';
import TruckProfileForm from 'src/components/MyTrucksComponents/TrucksProfileForm';
import PostDetailsForm from 'src/components/MyTrucksComponents/PostDetailsForm';
import { LoadingButton } from '@mui/lab';

import dayjs from 'dayjs';
import { api } from 'src/Api';
import moment from 'moment';
import { getDistance } from 'geolib';

export default function AddTrucks() {
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('user'));

  const location = useLocation();
  const { viewOnly, detail_id, data } = location.state || {};

  const [profileOpen, setProfileOpen] = useState(true);
  const [postOpen, setPostOpen] = useState(true);

  const [errors, setErrors] = useState({});

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isSaveLoading, setIsSaveLoading] = useState(null);
  const [loading, setloading] = useState(false);
  const [convertedLength, setConvertedLength] = useState('');
  const [convertedWeight, setConvertedWeight] = useState('');

  // profile form Data
  const [formData, setFormData] = useState({
    truckType: 'Full',
    equipmentType: '',
    length: '',
    length_unit: 'ft',
    weight: '',
    weight_unit: 'kg',
    origin: '',
    destination: '',
    dateEarliest: null,
    dateLatest: null,
    rate: '',
    comment: '',
    referenceId: '',
    contact: '',
    distance: ''
  });

  useEffect(() => {
    if (detail_id) {
      getTruckListData();
    }
  }, []);

  useEffect(() => {
    if (formData?.origin && formData?.destination) {
      if (formData?.origin?.address === formData?.destination?.address) {
        setFormData({
          ...formData,
          destination: null
        });
        setSnackbarMessage('Origin and Destination locations cannot be the same');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        if (
          formData?.origin &&
          formData?.origin &&
          formData?.destination &&
          formData?.destination
        ) {
          handleGetKilometers(
            formData?.origin?.lat,
            formData?.origin?.lon,
            formData?.destination?.lat,
            formData?.destination?.lon
          );
        }
      }
    }
  }, [formData?.origin, formData?.origin, formData?.destination, formData?.destination]);

  function getTruckListData() {
    setloading(true);
    const myHeaders = new Headers();

    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow'
    };

    const queryParams = new URLSearchParams();
    detail_id && queryParams.append('id', detail_id);

    fetch(`${api.get_trucks}?${queryParams.toString()}`, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200) {
          const viewOrigin = {
            address: result.data[0]?.location_origin,
            city: result.data[0]?.city_origin,
            country: result.data[0]?.country_origin,
            lat: parseFloat(result.data[0]?.lat_origin),
            lon: parseFloat(result.data[0]?.lon_origin),
            state: result.data[0]?.state_origin,
            zone: result.data[0]?.zone_origin,
            zip_code: result.data[0]?.zip_code || ''
          };
          const viewDetination = {
            address: result.data[0]?.location_destination,
            city: result.data[0]?.city_destination,
            country: result.data[0]?.country_destination,
            lat: parseFloat(result.data[0]?.lat_destination),
            lon: parseFloat(result.data[0]?.lon_destination),
            state: result.data[0]?.state_destination,
            zone: result.data[0]?.zone_destination,
            zip_code: result.data[0]?.zip_code || ''
          };
          setFormData({
            truckType: result.data[0]?.equipment_type,
            equipmentType: result.data[0]?.equipment_id,
            length: result.data[0]?.length_ft,
            weight: result.data[0]?.weight_lbs,
            length_unit: result.data[0]?.length_unit,
            weight_unit: result.data[0]?.weight_unit,
            origin: viewOrigin,
            destination: viewDetination,

            dateEarliest: result.data[0].startdate,
            dateLatest: result.data[0].enddate,
            rate: result.data[0].rate,

            comment: result.data[0]?.comments,
            referenceId: result.data[0]?.reference_id,
            // contact: result.data[0]?.contactno
            contact: Array.isArray(result.data[0].contact)
              ? result.data[0].contact.map((d) => d.id)
              : []
          });
          setloading(false);
        } else {
          setloading(false);
        }
      })
      .catch((error) => {
        console.log('error', error);
        setloading(false);
      });
  }

  const MAX_COMMENT_LENGTH = 140;

  // Handle input changes for equipment type, length, and weight
  const handleChangevalue = (e) => {
    const { name, value } = e.target;

    const maxLengthMap = {
      dho: 4,
      dhd: 4,
      length: 7,
      weight: 7,
      comment: MAX_COMMENT_LENGTH
    };

    if (maxLengthMap[name] && value.length > maxLengthMap[name]) {
      return;
    }

    // ---------- Length conversion ----------
    if (name === 'length') {
      setFormData((prev) => ({
        ...prev,
        length: value
      }));
      calculateConversion(value, formData.length_unit);
      return;
    }

    if (name === 'length_unit') {
      const newUnit = value;
      setFormData((prev) => ({
        ...prev,
        length_unit: newUnit
      }));
      calculateConversion(formData.length, newUnit);
      return;
    }

    // ---------- Weight conversion ----------
    if (name === 'weight') {
      setFormData((prev) => ({
        ...prev,
        weight: value
      }));
      calculateConversionWeight(value, formData.weight_unit);
      return;
    }

    if (name === 'weight_unit') {
      const newUnit = value;
      setFormData((prev) => ({
        ...prev,
        weight_unit: newUnit
      }));
      calculateConversionWeight(formData.weight, value);
      return;
    }

    // ---------- Default update ----------
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateConversionWeight = (weight, unit) => {
    if (!weight) {
      setConvertedWeight('');
      return;
    }
    const weightValue = parseFloat(weight);
    if (isNaN(weightValue)) return;

    let convertedValue = '';
    if (unit === 'lbs') {
      convertedValue = (weightValue * 0.453592).toFixed(2) + ' kg';
    } else if (unit === 'kg') {
      convertedValue = (weightValue / 0.453592).toFixed(2) + ' lbs';
    }
    setConvertedWeight(convertedValue);
  };

  const calculateConversion = (length, unit) => {
    if (!length) {
      setConvertedLength('');
      return;
    }
    const lengthValue = parseFloat(length);
    if (isNaN(lengthValue)) return;

    let convertedValue = '';
    if (unit === 'ft') {
      convertedValue = (lengthValue * 0.3048).toFixed(2) + ' m';
    } else if (unit === 'm') {
      convertedValue = (lengthValue / 0.3048).toFixed(2) + ' ft';
    }
    setConvertedLength(convertedValue);
  };

  // Handle radio button change for truckType
  const handleChangeRadio = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      truckType: e.target.value
    }));
  };

  // Validate form data
  const validate = () => {
    let tempErrors = {};
    tempErrors.truckType = formData?.truckType ? '' : 'Truck type is required.';
    tempErrors.equipmentType = formData?.equipmentType ? '' : 'Equipment type is required.';
    tempErrors.length = formData?.length ? '' : 'Length (ft.) is required.';
    tempErrors.weight = formData?.weight ? '' : 'Weight (lbs.) is required.';

    tempErrors.origin = formData?.origin ? '' : 'Origin is required.';
    tempErrors.destination = formData?.destination ? '' : 'Destination is required.';

    tempErrors.dateEarliest = formData?.dateEarliest ? '' : 'Pickup Earliest is required.';
    tempErrors.dateLatest = formData?.dateLatest ? '' : 'Pickup Latest is required.';

    // Convert the string dates to dayjs objects for comparison
    const dateEarliest = formData?.dateEarliest ? dayjs(formData?.dateEarliest) : null;
    const dateLatest = formData?.dateLatest ? dayjs(formData?.dateLatest) : null;

    // Check if both dates are provided and validate that latest date is after or the same as earliest
    if (dateEarliest && dateLatest) {
      if (dateLatest.isBefore(dateEarliest)) {
        tempErrors.dateLatest = 'Pickup Latest must be the same or after Pickup Earliest.';
      }
    }

    tempErrors.comment = formData?.comment ? '' : 'Comment is required.';
    tempErrors.referenceId = formData?.referenceId ? '' : 'Reference ID is required.';
    tempErrors.contact = formData?.contact ? '' : 'Contact is required.';

    // Validate rate input (should be a number and not empty)
    if (!formData?.rate) {
      tempErrors.rate = 'Rate is required.';
    } else if (isNaN(formData?.rate) || Number(formData?.rate) <= 0) {
      tempErrors.rate = 'Rate must be a valid positive number.';
    } else {
      tempErrors.rate = '';
    }

    setErrors(tempErrors);
    return Object.values(tempErrors).every((x) => x === '');
  };

  // close snackbarclose funtion
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // open profileform funtion
  const handleProfileOpen = () => {
    setProfileOpen(!profileOpen);
  };

  // open postform funtion
  const handlePostOpen = () => {
    setPostOpen(!postOpen);
  };

  //Date change function
  const handleDateRangeChange = (newDateRange) => {
    setFormData((prevData) => ({
      ...prevData,
      dateRange: newDateRange
    }));
  };

  // Change address function
  const handleAddressChange = (newAddress) => {
    setFormData((prevData) => ({
      ...prevData,
      origin: newAddress
    }));
  };

  // Change address destination function
  const handleAddressChangeDestination = (newAddress) => {
    setFormData((prevData) => ({
      ...prevData,
      destination: newAddress
    }));
  };

  const handleDateChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value ? value.toISOString() : null
    });
  };

  // Handle form submission
  const handleSubmit = (status, type) => {
    if (validate()) {
      AddNewTruckFunction(status, type);
    }
  };

  //add shipment function
  function AddNewTruckFunction(status, type) {
    setIsSaveLoading(type);
    const myHeaders = new Headers();

    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formdatas = new FormData();
    detail_id && formdatas.append('id', detail_id); // Required
    formdatas.append('type', formData?.truckType); // Required (Full | Partial)
    formdatas.append('equipment_type_id', formData?.equipmentType); // Required

    formdatas.append('length_ft', formData?.length); // Required
    formdatas.append('weight_lbs', formData?.weight); // Required

    formdatas.append('length_unit', formData?.length_unit); // Required
    formdatas.append('weight_unit', formData?.weight_unit); // Required

    formdatas.append('location_origin', formData?.origin?.address); // Required
    formdatas.append('country_origin', formData?.origin?.country); // Optional
    formdatas.append('city_origin', formData?.origin?.city); // Optional
    formdatas.append('state_origin', formData?.origin?.state); // Optional
    formdatas.append('lat_origin', formData?.origin?.lat); // Required
    formdatas.append('lon_origin', formData?.origin?.lon); // Required
    formdatas.append('zone_origin', formData?.origin?.zone); // Required

    formdatas.append('location_destination', formData?.destination?.address); // Required
    formdatas.append('country_destination', formData?.destination?.country); // Optional
    formdatas.append('city_destination', formData?.destination?.city); // Optional
    formdatas.append('state_destination', formData?.destination?.state); // Optional
    formdatas.append('lat_destination', formData?.destination?.lat); // Required
    formdatas.append('lon_destination', formData?.destination?.lon); // Required
    formdatas.append('zone_destination', formData?.destination?.zone); // Required

    formdatas.append('total_mil', formData?.distance);

    formdatas.append('startdate', moment(formData?.dateEarliest).format('YYYY-MM-DD')); // Required
    formdatas.append('enddate', moment(formData?.dateLatest).format('YYYY-MM-DD')); // Required

    formdatas.append('rate', formData?.rate); // Required
    formdatas.append('comments', formData?.comment); // Optional
    formdatas.append('reference_id', formData?.referenceId); // Optional
    formdatas.append('contact_id', formData?.contact); // Required
    status && formdatas.append('status', status);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdatas,
      redirect: 'follow'
    };

    fetch(api.save_truck, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          setSnackbarMessage(result.message);
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setIsSaveLoading(null);
          setTimeout(() => {
            navigate(-1);
          }, 2000);
        } else {
          setIsSaveLoading(null);
          setSnackbarMessage(result.message);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      })
      .catch((error) => {
        setIsSaveLoading(null);
        setSnackbarMessage('An error occurred.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  }

  const handleGetKilometers = (originLat, originLng, destinationLat, destinationLng) => {
    // 🔐 Guard clause
    if (
      originLat == null ||
      originLng == null ||
      destinationLat == null ||
      destinationLng == null
    ) {
      console.warn('Invalid coordinates', {
        originLat,
        originLng,
        destinationLat,
        destinationLng
      });
      return;
    }

    const origin = {
      latitude: Number(originLat),
      longitude: Number(originLng)
    };

    const destination = {
      latitude: Number(destinationLat),
      longitude: Number(destinationLng)
    };

    const distanceInMeters = getDistance(origin, destination);
    const distanceInKilometers = distanceInMeters / 1000;

    setFormData((prev) => ({
      ...prev,
      distance: distanceInKilometers.toFixed(2)
    }));
  };

  return (
    <Page
      title={`${
        (viewOnly && 'View Details') || (!viewOnly && detail_id && 'Edit Truck') || 'Add New Truck'
      } | ${process.env.REACT_APP_PROJECTNAME}`}
    >
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="flex-start" mb={2}>
          <IconButton
            style={{
              marginRight: 12,
              height: 40,
              width: 70,
              borderRadius: 5,
              backgroundColor: 'rgba(209,209,209,0.3)'
            }}
            onClick={() => navigate(-1)}
          >
            <Iconify icon="ion:return-up-back" style={{ fontSize: 25, color: '#010f58' }} />
          </IconButton>
          <Typography variant="h4" style={{ flex: 1, color: '#010f58' }}>
            {(viewOnly && 'View Details') ||
              (!viewOnly && detail_id && 'Edit Truck') ||
              'Add New Truck'}
          </Typography>
          {/* {!viewOnly && (
            <LoadingButton variant="contained" onClick={handleSubmit} loading={isSaveLoading}>
              {!viewOnly && detail_id ? 'Update' : 'Submit'}
            </LoadingButton>
          )} */}
        </Stack>

        {/* loader */}
        {loading ? (
          <Box
            py={5}
            style={{
              display: 'flex',
              justifyContent: 'center',
              flex: 1
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Scrollbar style={{ paddingLeft: 10, paddingRight: 10, paddingBottom: 30 }}>
            <TruckProfileForm
              disabled={viewOnly}
              setFormData={setFormData}
              Profileopen={profileOpen}
              formData={formData}
              errors={errors}
              handleProfileOpen={handleProfileOpen}
              handleChangeRadio={handleChangeRadio} // Handling radio change
              handleChangevalue={handleChangevalue} // Handling other input changes
              handleSubmit={handleSubmit} // Submit form
              convertedLength={convertedLength}
              convertedWeight={convertedWeight}
            />

            <PostDetailsForm
              disabled={viewOnly}
              detail_id={detail_id}
              postOpen={postOpen}
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              handleChangevalue={handleChangevalue} // Handling other input changes
              handlePostOpen={handlePostOpen} // Handling radio change
              handleDateRangeChange={handleDateRangeChange}
              onResultAddress={(val) => {
                handleAddressChange(val);
              }}
              onResultAddressDestination={(val) => {
                handleAddressChangeDestination(val);
              }}
              MAX_COMMENT_LENGTH={MAX_COMMENT_LENGTH}
              handleDateChange={handleDateChange}
            />
          </Scrollbar>
        )}

        {!viewOnly && !loading && detail_id && (
          <Stack direction="row" alignItems="center" justifyContent="flex-end" px={1}>
            <LoadingButton
              variant="contained"
              loading={isSaveLoading == 'save'}
              onClick={() =>
                handleSubmit(
                  data?.status?.toLowerCase() == 'pending' ? 'draft' : data?.status,
                  'save'
                )
              }
              disabled={viewOnly}
              style={{ backgroundColor: '#010f58' }}
            >
              Post Truck
            </LoadingButton>
            {data.status.toLowerCase() == 'pending' && (
              <LoadingButton
                variant="contained"
                loading={isSaveLoading == 'exit'}
                onClick={() => handleSubmit(data.status, 'exit')}
                disabled={viewOnly}
                style={{ backgroundColor: '#010f58', marginLeft: 20 }}
              >
                Save and exit
              </LoadingButton>
            )}
          </Stack>
        )}
        {!viewOnly && !loading && !detail_id && (
          <Stack direction="row" alignItems="center" justifyContent="flex-end" px={1}>
            <LoadingButton
              variant="contained"
              loading={isSaveLoading == 'save'}
              onClick={() => handleSubmit('1', 'save')}
              disabled={viewOnly}
              style={{ backgroundColor: '#010f58' }}
            >
              Post Truck
            </LoadingButton>
            <LoadingButton
              variant="contained"
              loading={isSaveLoading == 'exit'}
              onClick={() => handleSubmit('11', 'exit')}
              disabled={viewOnly}
              style={{ backgroundColor: '#010f58', marginLeft: 20 }}
            >
              Save and exit
            </LoadingButton>
          </Stack>
        )}

        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Page>
  );
}
